<template>
  <div>
    <div>
      <div class="d-flex justify-space-between">
        <BaseButton
          :text="`Back to 'Your details'`"
          flat
          ripple
          class="font-weight-bold d-flex ml-n5"
          icon="mdi-chevron-left"
          color="#106175"
          small
          @click="backToYourDetailsStep"
        />
        <BaseButton
          :text="$vuetify.breakpoint.lgAndUp ? 'Save &amp; exit' : 'Save'"
          flat
          depressed
          ripple
          class="font-weight-bold px-1 py-3"
          icon="mdi-content-save"
          color="#106175"
          small
          @click="toggleGlobalSaveAndExitDialog"
        />
      </div>
      <h2 class="font-24 font-lg-28 font-weight-bold primary--text mt-10">
        Cover details
      </h2>
    </div>

    <ValidationObserver
      ref="observerFormPropertyDetails"
      v-slot="{ validate, invalid }"
    >
      <v-form
        ref="form"
        @submit.prevent="validate().then(() => onSubmit(invalid))"
      >
        <div :class="isEnteringAddressManually ? 'mt-10' : ''">
          <template
            v-if="isShowInsuredAddressLabel && !isEnteringAddressManually"
          >
            <BaseLabel class="mt-10" text="Find the property to be insured" />
            <p
              class="d-flex align-left flex-no-wrap font-14 description"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '630px' : '100%',
              }"
            >
              {{ ADDRESS_FINDER_INPUT_DESCRIPTION }}
            </p>
          </template>
          <BaseAddressLookup
            id="address-lookup-insuredAddress"
            data-uiid="InsuredAddress"
            manualFormUFiid="InsuredAddress"
            :placeholder="
              $vuetify.breakpoint.lgAndUp
                ? 'Start typing your address or postcode...'
                : 'Start typing...'
            "
            showManualAddressForm
            manualAddressFormText="Your address"
            :hideDetails="false"
            :address="insuredAddress"
            @address-selected="
              (response) => getAddressSelected(response, insuredAddress)
            "
            @is-address-selected="insuredAddressLabel"
            @use-address-finder="isEnteringAddressManually = false"
            @enter-address-manually="isEnteringAddressManually = true"
            :rules="{
              required: {
                required: true,
                fieldLabel: 'Find the property to be insured',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '500px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          />
        </div>

        <BaseLabel
          :class="
            isEnteringAddressManually || isShowInsuredAddressLabel
              ? 'mt-3'
              : 'mt-10'
          "
          text="Monthly rental payment"
        />
        <p
          class="d-flex align-left flex-no-wrap font-14 description"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '500px' : '100%',
          }"
        >
          Please enter the agreed monthly rent amount in full as per the tenancy
          agreement.
        </p>
        <BaseInput
          ref="questionMonthlyRentalPaymentRef"
          :vid="questionMonthlyRentalPayment.InterfaceKey"
          v-model="questionMonthlyRentalPayment.Value"
          :value="questionMonthlyRentalPayment.Value"
          prefix="£"
          moneyFormat
          dataUiid="questionMonthlyRentalPayment"
          :hideDetails="false"
          :rules="{
            required: {
              required: true,
              fieldLabel: 'Monthly rental payment',
            },
            regex: {
              required: true,
              pattern: NUMBER_ONLY_REGEX,
              customMessage: 'Monthly rental payment field format is invalid.',
            },
            regex: {
              required: true,
              pattern: '^[0-9,]+(.[0-9]{1,2})?$',
              customMessage:
                'Only numbers up to two decimal places are allowed in this field.',
            },
            min_currency_value: {
              minValue: 1.0,
              inclusive: true,
              fieldLabel: 'Monthly rental payment',
            },
          }"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '160px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
        />

        <BaseAlert
          color="placeboblue"
          class="mb-0 py-3 pr-3 border-r10"
          :maxWidth="$vuetify.breakpoint.lgAndUp ? '500px' : '100%'"
        >
          <div class="d-flex flex-no-wrap">
            <div class="mx-3">
              <v-icon color="cyanazure">mdi-information</v-icon>
            </div>
            <div>
              <span class="font-weight-bold">Note: </span>
              Claims are capped at £2,500 where the rental amount exceeds this
              value.
            </div>
          </div>
        </BaseAlert>

        <BaseLabel
          class="mt-10 mb-1"
          text="Is this policy to be purchased in conjunction with a new tenancy agreement, or replacing a comparable policy?"
          helpText="Please answer yes if this policy is either: <br />
          <ul>
            <li>To begin prior to or on the start date of a new tenancy agreement, or</li>
            <li>Is directly replacing a comparable policy meaning there is no break in cover between the cancellation date of the existing policy and start date of this policy.</li>
          </ul>"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '630px' : '100%',
          }"
        />
        <BaseToggleButton
          v-model="questionNewLetOrProductTransfer.Value"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
          styleClass="mb-4"
          :hide-details="false"
          :toggleButtonText="{
            yes: 'Yes',
            no: 'No',
            yesDataUiid: `${questionNewLetOrProductTransfer.InterfaceKey}_Yes`,
            noDataUiid: `${questionNewLetOrProductTransfer.InterfaceKey}_No`,
          }"
        />

        <template v-if="!questionNewLetOrProductTransfer.Value">
          <BaseAlert
            color="#FFF1E4"
            class="py-3 border-r10 mb-0"
            :maxWidth="$vuetify.breakpoint.lgAndUp ? '500px' : '100%'"
          >
            <div class="d-flex flex-no-wrap">
              <div class="mr-3">
                <v-icon color="#fb8c00">mdi-alert</v-icon>
              </div>
              <div
                class="black2-color"
                :class="$vuetify.breakpoint.lgAndUp ? 'mr-1' : ''"
              >
                <span class="font-weight-bold">Important!</span>
                As you have answered 'No' to this question, please be aware that
                any dispute arising within the first 90 days of the policy is
                excluded.
              </div>
            </div>
          </BaseAlert>

          <BaseCheckBox
            className="font-weight-bold primary--text mb-1"
            label="I confirm that I have read this to the applicant."
            hideDetails="auto"
            :checked="rentCoverConfirmation"
            :rules="{
              required: {
                required: true,
                fieldLabel: 'Confirmation',
              },
            }"
            @change="(val) => setConfirmationRentCover(val)"
          />
        </template>

        <BaseButton
          rounded
          class="mt-5 font-weight-bold continue-button primary--text continueBtn"
          height="60"
          type="submit"
          text="GET QUOTE"
          :color="'psorange'"
          :fontSize="'font-20'"
          :disabled="
            !questionNewLetOrProductTransfer.Value && !rentCoverConfirmation
          "
          :min-width="$vuetify.breakpoint.lgAndUp ? '263' : '100%'"
        />
      </v-form>
    </ValidationObserver>

    <EligibilityBaseDialog ref="dialog" :windowClass="'confirm-dialog'">
      <template v-slot:content="{ cancel, isShow, title }">
        <Eligibility
          :cancel="cancel"
          :dialogRef="$refs.dialog"
          :dialogLoading="$refs.dialogLoading"
          :dialogNotMet="$refs.dialogNotMet"
          :isShow="isShow"
          :listEligibility="RENT_PROTECTION_ELIGIBILITY"
          :inforMsg="RENT_PROTECTION_ELIGIBILITY_INFO_MSG"
          :title="title"
          :useRawHtml="true"
          @confirmEligibility="createQuote()"
          :customHandleConfirmEligibility="true"
        />
      </template>
    </EligibilityBaseDialog>

    <EligibilityBaseDialog ref="dialogLoading" :windowClass="'confirm-dialog'">
      <template v-slot:content="{ title }">
        <EligibilityLoading
          :title="title"
          backgroundMessageColor="#EBF5FD"
          imageSource=""
          :gettingQuoteMessage="GETTING_QUOTE_RENT_PROTECTION_MESSAGE"
        />
      </template>
    </EligibilityBaseDialog>

    <EligibilityBaseDialog ref="dialogNotMet" :windowClass="'confirm-dialog'">
      <template v-slot:content="{ cancel, isShow, title }">
        <EligibilityNotMet
          :cancel="cancel"
          :dialogRef="$refs.dialog"
          :dialogNotMet="$refs.dialogNotMet"
          :isShow="isShow"
          :title="title"
        />
      </template>
    </EligibilityBaseDialog>
  </div>
</template>

<script>
import moment from "moment";
import { EventBus } from "@/common/eventbus.js";
import {
  convertToBool,
  scrollTop,
  sanitizeCurrencyValue,
} from "@/common/utils";
import { ValidationObserver } from "vee-validate";
import { mapActions, mapState } from "vuex";
import { addressSelected } from "@/common/addresses";

import { NUMBER_ONLY_REGEX } from "@/constants/regex-patterns.constants";
import { ADDRESS_FINDER_INPUT_DESCRIPTION } from "@/constants/description.constants";
import {
  RENT_PROTECTION_ELIGIBILITY,
  RENT_PROTECTION_ELIGIBILITY_INFO_MSG,
} from "@/constants/eligibility.constants";
import * as LABEL_CONSTANTS from "@/constants/label.constants";
import { GETTING_QUOTE_RENT_PROTECTION_MESSAGE } from "@/constants/message.constants";
import { ROUTE_NAME } from "@/constants/route.constants";
import { QUESTIONS_MODULE } from "@/store/types/module-types";

import BaseAddressLookup from "@/components/BaseComponents/BaseAddressLookup.vue";
import BaseButton from "@/components/BaseComponents/BaseButton.vue";
import BaseLabel from "@/components/BaseComponents/BaseLabel.vue";
import BaseInput from "@/components/BaseComponents/BaseInput.vue";
import BaseAlert from "@/components/BaseComponents/BaseAlert.vue";
import BaseToggleButton from "@/components/BaseComponents/BaseToggleButton.vue";
import BaseCheckBox from "@/components/BaseComponents/BaseCheckBox.vue";
import EligibilityBaseDialog from "@/components/EligibilityBaseDialog";
import Eligibility from "@/views/Dialog/Eligibility.vue";
import EligibilityLoading from "@/views/Dialog/EligibilityLoading";
import EligibilityNotMet from "@/views/Dialog/EligibilityNotMet.vue";

export default {
  name: "RentProtectionCoverDetailsPage",
  components: {
    BaseButton,
    BaseLabel,
    BaseInput,
    BaseAlert,
    BaseToggleButton,
    BaseCheckBox,
    ValidationObserver,
    BaseAddressLookup,
    EligibilityLoading,
    EligibilityNotMet,
    EligibilityBaseDialog,
    Eligibility,
  },
  data() {
    return {
      LABEL_CONSTANTS,
      RENT_PROTECTION_ELIGIBILITY,
      RENT_PROTECTION_ELIGIBILITY_INFO_MSG,
      GETTING_QUOTE_RENT_PROTECTION_MESSAGE,
      NUMBER_ONLY_REGEX,
      ADDRESS_FINDER_INPUT_DESCRIPTION,
      isShowInsuredAddressLabel: true,
      isEnteringAddressManually: false,
      insuredAddress: {},
      questionMonthlyRentalPayment: {},
      questionNewLetOrProductTransfer: {},
    };
  },
  computed: {
    ...mapState(QUESTIONS_MODULE, [
      "restoringLastStep",
      "rentCoverConfirmation",
    ]),
    ...mapState(QUESTIONS_MODULE, ["questionMap"]),
  },
  created() {
    this.onSetupAndLoadSavedData();
  },
  beforeMount() {
    scrollTop();
  },
  mounted() {
    this.setRestoringLastStep(false);
    this.setCurrentRefObserverForm(this.$refs.observerFormPropertyDetails);
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "getQuotes",
      "saveProgress",
      "setRestoringLastStep",
      "setIsExpiredFlag",
      "toggleGlobalSaveAndExitDialog",
      "setCurrentRefObserverForm",
      "refreshSessionAndNextStep",
      "setConfirmationRentCover",
    ]),
    backToYourDetailsStep() {
      this.setRestoringLastStep(false);
      this.$router.push({
        name: ROUTE_NAME.RENT_PROTECTION.B2B.YOUR_DETAILS,
      });
    },
    onSetupAndLoadSavedData() {
      // insured address
      this.insuredAddress.PostCode = this.questionMap["InsuredAddressPostCode"];
      this.insuredAddress.Street = this.questionMap["InsuredAddressStreet"];
      this.insuredAddress.District = this.questionMap["InsuredAddressDistrict"];
      this.insuredAddress.Town = this.questionMap["InsuredAddressTown"];
      this.insuredAddress.County = this.questionMap["InsuredAddressCounty"];

      this.questionMonthlyRentalPayment = this.questionMap["MonthlyRent"];
      this.questionNewLetOrProductTransfer =
        this.questionMap["NewLetOrProductTransfer"];

      // cook data
      this.questionNewLetOrProductTransfer.Value = convertToBool(
        this.questionNewLetOrProductTransfer.Value
      );
    },

    async onSubmit(invalid) {
      if (invalid) {
        EventBus.$emit("validation-error", "Validation error occurred");
        return;
      }
      this.$refs.dialog.open("Eligibility check");
    },

    createQuote() {
      this.$refs.dialog.cancel();
      this.$refs.dialogLoading.open("Hang tight, getting quotes...");

      this.questionMap["PolicyStartDate"].Value = moment().format("DD/MM/YYYY");
      this.questionMap["MonthlyRent"].Value = sanitizeCurrencyValue(
        this.questionMap["MonthlyRent"].Value
      );

      const quoteRequestFields = {
        IsPartialQuote: false,
      };

      this.getQuotes(quoteRequestFields)
        .then(() => {
          this.setIsExpiredFlag(false);
          this.$refs.dialog.cancel();
          this.refreshSessionAndNextStep(
            ROUTE_NAME.RENT_PROTECTION.B2B.QUOTATION_RESULT
          );
        })
        .catch(() => {
          this.$refs.dialogLoading.cancel();
        });
    },

    getAddressSelected(response, address) {
      addressSelected(response, address);
    },
    insuredAddressLabel(val) {
      this.isShowInsuredAddressLabel = !val;
    },
  },
};
</script>

<style lang="scss" scoped>
.description {
  letter-spacing: 0.4px;
  margin-bottom: 13px;
  max-width: 665px;
  color: #272727;
}

@media screen and (max-width: ($grid-breakpoints-sm - 1)) {
  :deep(.v-input--radio-group__input) {
    justify-content: space-between;
    button {
      margin-right: 0 !important;
    }
  }
}
</style>
